import { inject } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot, CanActivateFn } from '@angular/router';
import { createAuthGuard, AuthGuardData } from 'keycloak-angular';
import Keycloak from 'keycloak-js';

/**
 * Custom access check function for the Keycloak auth guard.
 * Validates authentication and role-based access.
 */
const isAccessAllowed = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  authData: AuthGuardData
): Promise<boolean> => {
  const { authenticated, grantedRoles } = authData;
  const keycloak = inject(Keycloak);
  const router = inject(Router);
  
  // If not authenticated, redirect to Keycloak login
  if (!authenticated) {
    try {
      await keycloak.login({
        redirectUri: window.location.origin + state.url
      });
    } catch (error) {
      console.error('Keycloak login failed:', error);
      return false;
    }
    return false;
  }

  // Get the roles required from the route
  const requiredRoles = route.data['roles'] as string[];

  // Allow the user to proceed if no roles are required
  if (!requiredRoles || requiredRoles.length === 0) {
    return true;
  }

  // Get all user roles (realm + resource roles)
  const realmRoles = grantedRoles.realmRoles || [];
  const resourceRoles = Object.values(grantedRoles.resourceRoles || {}).flat();
  const allUserRoles = [...realmRoles, ...resourceRoles];

  // Allow the user to proceed if all the required roles are present
  const hasAllRoles = requiredRoles.every((role) => allUserRoles.includes(role));
  
  if (!hasAllRoles) {
    // Redirect to home page with unauthorized flag
    router.navigate(['/accueil'], {
      queryParams: { unauthorized: true }
    });
    return false;
  }

  return true;
};

/**
 * Functional auth guard using keycloak-angular v19+ API
 */
export const AuthGuard = createAuthGuard<CanActivateFn>(isAccessAllowed);